import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Filter, X } from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";

/**
 * useUrlFilters — filter state that lives in the URL (Phase 84 dashboard drilldowns).
 *
 * - Initialises from the current query string, so a dashboard chart can deep-link
 *   `/admin/properties?ward_id=…` and the page opens pre-filtered.
 * - Every change is written back with `replace`, so the Back button still returns to
 *   the previous page (the dashboard) instead of stepping through each filter edit,
 *   while the address bar always holds a shareable link.
 * - `strip` lists one-shot params (e.g. Phase 65 `?focus=`) that must never linger.
 *
 * `keys` / `strip` must be module-level constants (stable identity).
 */
const NO_STRIP = [];

export const useUrlFilters = (keys, { strip = NO_STRIP } = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState(() =>
    Object.fromEntries(keys.map((k) => [k, searchParams.get(k) || ""])));
  // did the visitor ARRIVE with filters in the link (i.e. a drilldown / shared URL)?
  const arrivedFiltered = useRef(keys.some((k) => searchParams.get(k))).current;

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    keys.forEach((k) => (filters[k] ? next.set(k, filters[k]) : next.delete(k)));
    strip.forEach((k) => next.delete(k));
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [filters, searchParams, setSearchParams, keys, strip]);

  const active = keys.filter((k) => filters[k]);
  const clear = () => setFilters(Object.fromEntries(keys.map((k) => [k, ""])));
  const patch = (changes) => setFilters((f) => ({ ...f, ...changes }));
  return { filters, setFilters, patch, clear, active, arrivedFiltered };
};

/**
 * DrilldownBanner — "Showing filtered results" strip with one chip per active filter
 * and a Clear action. Rendered above list pages whenever filters are in effect.
 * `chips`: [{ key, label, value }] (only chips with a value are shown).
 */
export const DrilldownBanner = ({ chips = [], onClear, testId = "drilldown-banner", note }) => {
  const shown = chips.filter((c) => c.value);
  if (shown.length === 0) return null;
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2"
      role="status" data-testid={testId}>
      <Filter className="h-4 w-4 text-primary shrink-0" aria-hidden="true" />
      <span className="text-sm font-medium">Showing filtered results</span>
      {note && <span className="text-xs text-muted-foreground">{note}</span>}
      <div className="flex flex-wrap items-center gap-1.5">
        {shown.map((c) => (
          <Badge key={c.key} variant="outline" className="bg-background font-normal gap-1" data-testid={`${testId}-chip-${c.key}`}>
            <span className="text-muted-foreground">{c.label}:</span> <span className="font-medium">{c.value}</span>
          </Badge>
        ))}
      </div>
      <Button variant="ghost" size="sm" className="ml-auto h-7 text-xs" onClick={onClear} data-testid={`${testId}-clear`}>
        <X className="h-3.5 w-3.5 mr-1" /> Clear filters
      </Button>
    </div>
  );
};
